import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AddFormFieldButton } from '@/components/Buttons/AddFormFieldButton/AddFormFieldButton';
import { EntityType } from '@/core/graphql/generated/types';

import { useDcrContext } from '../../_context/DcrContext';
import type { AffiliationsSectionValuesIn } from '../../_schemas/AffiliationsSection.validation';
import { FormSection } from '../../DcrModal/FormSection/FormSection';

import { AffiliationsSectionCard } from './AffiliationsSections/AffiliationsSectionsCard';
import { affiliationsSectionStyles as sx } from './AffiliationsSections.styles';

type AffiliationSnapshot = AffiliationsSectionValuesIn['parenthcos'][number];

type Props = {
	label?: string;
	entityType: EntityType;
};

export const AffiliationsSection = ({ label, entityType }: Props) => {
	const { isSimpleView } = useDcrContext();
	const { t } = useTranslation();
	const { control, setValue } = useFormContext<AffiliationsSectionValuesIn>();

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'parenthcos'
	});

	const [initialSnapshots, setInitialSnapshots] = useState<Record<
		string,
		AffiliationSnapshot
	> | null>(null);

	useEffect(() => {
		if (initialSnapshots) return;

		if (fields.length > 0) {
			const snapshot: Record<string, AffiliationSnapshot> = {};
			const stableData = JSON.parse(JSON.stringify(fields));

			stableData.forEach((field: AffiliationSnapshot) => {
				if (field.vid_V) {
					snapshot[field.vid_V] = field;
				}
			});

			// eslint-disable-next-line react-hooks/set-state-in-effect
			setInitialSnapshots(snapshot);
		}

		if (fields.length === 0) {
			setInitialSnapshots({});
		}
	}, [fields, initialSnapshots]);

	const handleAddAffiliation = () => {
		append(
			{
				vid_V: '',
				// Parent Data
				parentHcoVid: '',
				hcoNameCda_V: '',
				hcoType: '',
				hcoStatus: 'Active',
				location: '',
				addresses: [],
				// Relationship Data
				jobTitle_V: '',
				typeOfRelationship_VId: '',
				classOfWork_V: '',
				isPrimaryRelationship_V: null
			},
			{ shouldFocus: false }
		);
	};

	const handleSetPrimary = (indexToMakePrimary: number) => {
		fields.forEach((_, idx) => {
			const isPrimary = idx === indexToMakePrimary;
			setValue(`parenthcos.${idx}.isPrimaryRelationship_V`, isPrimary, {
				shouldDirty: true
			});
		});
	};

	return (
		<FormSection title={label ?? t('Affiliations')} id='affiliations'>
			<Box sx={sx.root}>
				{isSimpleView && (
					<Typography variant='body2' sx={sx.label}>
						{label ?? t('Affiliations')}
					</Typography>
				)}

				<Box sx={sx.fieldWrapper}>
					{fields.length > 0 && (
						<Box sx={sx.cardsContainer}>
							{fields.map((field, index) => {
								const originalData = initialSnapshots?.[field.vid_V ?? ''];
								const isNewCard = !originalData;

								return (
									<AffiliationsSectionCard
										key={field.id}
										index={index}
										onRemove={() => remove(index)}
										canBeRemoved={isNewCard}
										onSetPrimary={() => handleSetPrimary(index)}
										isExpanded={isNewCard}
										originalData={originalData}
										isNewCard={isNewCard}
										isLastItem={index === fields.length - 1}
										entityType={entityType}
									/>
								);
							})}
						</Box>
					)}

					<Box sx={sx.addNewCardButton}>
						<AddFormFieldButton onClick={handleAddAffiliation} />
					</Box>
				</Box>
			</Box>
		</FormSection>
	);
};
